import React, { FC, ReactElement, useEffect } from 'react';
import { Action, Dispatch } from 'redux';
import { connect } from 'react-redux';
import StateInterface from '../../redux/types';
import { ListingPageUrlState, ProductsState } from '../../redux/products/types';
import useGa from '../../hooks/useGa';
import { PageType } from '../../hooks/UrlManager/types';
import usePrevious from '../../hooks/usePrevious';
import { fetchProductsDataIfNeeded } from '../../redux/products/actions';
import { fetchContentInfusionIfNeeded } from '../../redux/infusions/actions';
import { NormalizedCategoriesData } from '../../redux/category/types';
import useFetchingAction from '../../hooks/useFetchingAction/useFetchingAction';
import useConfig from '../../hooks/useConfig';
import useBellaTranslator from '../../hooks/useTranslator';
import { combineThunks } from '../../redux/utils';
import { fetchSeoDataIfNeeded } from '../../redux/seo/actions';
import { useBrazePlpEvent } from '../../hooks/Braze/useBraze';
import { fetchCMSLinkedBannersIfNeeded } from '../../redux/cms/actions';
import { SHOP_TO_CLUB_FOOTER_LINK } from '../../redux/cms/constants';
import { Dictionary } from '../../../../types/globals';
import { getEntityByUrlKey } from '../../redux/category/categoriesNormalizer';
import { RedirectSuggestions } from '../../redux/dynamicSearch/types';
import { clearDynamicSearchMetrics, isRealDynamicSearchAction } from '../../redux/dynamicSearch/actions';
import useHotjar from '../ThirdPartyIntegration/useHotjar';
import useTrackingPageLabel from './hooks/useTrackingPageLabel';
import { getDefaultSeoData } from './services/seo';
import useListingPageUrlState from './hooks/useListingPageUrlState';
import ListingPage from './ListingPage';
import NoResultsPage from './NoResults';
import NoFilterResultsPage from './NoFilterResults';
import useLandingPageRedirects from './redirectsHook';
import { buildDynamicSearchGaData } from './SearchTracking';
import { useListingPageInfusions } from './hooks/useListingPageInfusions';
import { convertListingPageStateToUrl } from './services/listingPageUrlService';
import { useBellaSelector } from 'Client/redux/hooks';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';

interface ReduxStateProps {
    dynamicSearchRedirectSuggestions: RedirectSuggestions;
    isRealDynamicSearch: boolean;
    clickedSuggestion: string;
    products: ProductsState;
    brandEntities: Dictionary<string> | {};
    categories: NormalizedCategoriesData | null;
}

interface ReduxDispatchProps {
    clearDynamicSearchMetrics: () => Action;
    setRealDynamicSearch: (value: boolean) => Action;
}

const buildPageViewData = (page: number): Dictionary<string> => {
    return { page_number: page.toString() };
};

// eslint-disable-next-line complexity
const DynamicListingPage: FC<{} & ReduxStateProps & ReduxDispatchProps> = (props): ReactElement | null => {
    const {
        products,
        dynamicSearchRedirectSuggestions,
        categories,
        brandEntities,
        isRealDynamicSearch,
        clearDynamicSearchMetrics,
        clickedSuggestion,
    } = props;
    const [urlState] = useListingPageUrlState();
    const pageType = useTrackingPageLabel();
    const gaTracking = useGa();
    const { p100PlpLooksInfusion } = useFeatureToggle();
    const amFiltering = urlState.appliedFilters.length > 0;
    const isAllProducts = urlState.listingPageType === PageType.ALLPRODUCTS;
    const { isFetching, isFetchingComplete, trackingData } = products;
    const isNoResults = isAllProducts && isFetchingComplete && !products.products.length;
    const isNoFilterResults =
        !isFetching &&
        isFetchingComplete &&
        !products.products.length &&
        !products.alternativeProducts.length &&
        amFiltering;

    const wasNoResults = usePrevious(isNoResults);
    const didNoResultsBecomeTrue = !wasNoResults && isNoResults;

    const config = useConfig();
    const t = useBellaTranslator();
    const thunks = [
        fetchProductsDataIfNeeded(urlState, {
            countryCode: config.countryCode,
            currency: config.currency,
            includeLooksInfusions: !!p100PlpLooksInfusion,
        }),
        fetchSeoDataIfNeeded(urlState, getDefaultSeoData(urlState.listingPageType, t, config)),
        fetchCMSLinkedBannersIfNeeded(SHOP_TO_CLUB_FOOTER_LINK),
    ];

    const { infusionEnabled } = useListingPageInfusions();

    if (infusionEnabled) {
        thunks.push(fetchContentInfusionIfNeeded(urlState));
    }

    useFetchingAction(combineThunks(...thunks));

    useEffect(() => {
        if (didNoResultsBecomeTrue) {
            gaTracking.dynamicSearch.trackSearchWithNoResult(urlState.search);
        }
    }, [didNoResultsBecomeTrue, gaTracking, urlState.search]);

    useEffect(() => {
        const query = urlState.search;

        if (query) {
            gaTracking.dynamicSearch.trackSearchUrlParam(query);
        }
    }, [gaTracking, urlState.search]);

    const wasRedirected = useLandingPageRedirects();

    const buildGaData = (
        urlState: ListingPageUrlState,
        categories: NormalizedCategoriesData | null,
        brandEntities: Dictionary<string> | {}
    ): Dictionary<string> => {
        /* eslint-disable @typescript-eslint/camelcase, camelcase */
        const { page, listingPageKey } = urlState;
        const pageViewData = buildPageViewData(page);
        switch (urlState.listingPageType) {
            case PageType.CATEGORY:
                return {
                    categories_path: listingPageKey,
                    category_id: categories ? categories.urlKeys[listingPageKey] : '',
                    ...pageViewData,
                };
            case PageType.BRANDS:
                return { brand_id: brandEntities[listingPageKey] || '', ...pageViewData };
            default:
                return pageViewData;
        }
        /* eslint-enable @typescript-eslint/camelcase, camelcase */
    };

    useEffect(() => {
        /**
         * Page view needs to be fired only when
         * API fetch has been completed, checked via isFetchingComplete
         * but when moving client side say PLP -> Home -> PLP the component
         * is remounted and at that time in store isFetchingComplete is true
         * To make sure at that point page view is not fired, we put identifier
         * matching condition, when identifier in URL and store is same, the
         * store is properly updated and safe to trigger pageView.
         */

        const identifier = convertListingPageStateToUrl(urlState);
        if (!isFetchingComplete || identifier !== products.identifier) {
            return;
        }

        const dynamicSearchData = buildDynamicSearchGaData(
            urlState.search || '',
            products,
            dynamicSearchRedirectSuggestions,
            isRealDynamicSearch,
            clickedSuggestion
        );
        const gaData = buildGaData(urlState, categories, brandEntities);

        gaTracking.trackPageView({
            pageType,
            gaData: {
                ...gaData,
                ...dynamicSearchData,
                ww_req_id: trackingData?.wwReqId || '',
                search_respose_id: trackingData?.searchResponseId || '',
            },
        });

        if (isRealDynamicSearch || clickedSuggestion) {
            clearDynamicSearchMetrics();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetchingComplete]);

    const categoryEntity = getEntityByUrlKey(categories, urlState.listingPageKey);
    useBrazePlpEvent(categoryEntity);

    // Trigger hotjar overlays
    const isLoggedIn = useBellaSelector(state => state.appShellLogin.isLoggedIn);
    const { sendOverlayTrigger } = useHotjar();
    useEffect(() => {
        if (isLoggedIn) {
            sendOverlayTrigger('plp__page-view--logged-in');
        }
    }, [isLoggedIn, sendOverlayTrigger]);
    sendOverlayTrigger('plp__page-view');

    if (wasRedirected) {
        return null;
    }
    if (isNoResults) {
        return <NoResultsPage />;
    }
    if (isNoFilterResults) {
        return <NoFilterResultsPage />;
    }
    return <ListingPage />;
};

const mapStateToProps = (state: StateInterface): ReduxStateProps => ({
    dynamicSearchRedirectSuggestions: state.dynamicSearchSuggestions.searchRedirect,
    isRealDynamicSearch: state.dynamicSearchSuggestions.isRealSearch,
    clickedSuggestion: state.dynamicSearchSuggestions.clickedSuggestion,
    products: state.products,
    brandEntities: state.brands.brandEntities,
    categories: state.categories.normalized,
});

const mapDispatchToProps = (dispatch: Dispatch): ReduxDispatchProps => ({
    clearDynamicSearchMetrics: (): Action => dispatch(clearDynamicSearchMetrics()),
    setRealDynamicSearch: (value: boolean): Action => dispatch(isRealDynamicSearchAction(value)),
});
const Connected = connect(mapStateToProps, mapDispatchToProps)(DynamicListingPage);

export default Connected;
